import React, { useState } from 'react';
import { ArrowLeft, Shield, Clock, TrendingUp, CheckCircle, Building, Banknote, Users, Calendar } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Slider } from '../components/ui/slider';

const FixedDepositsPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  
  // Compound Interest Calculator State
  const [principalAmount, setPrincipalAmount] = useState(100000);
  const [annualRate, setAnnualRate] = useState(15);
  const [compoundingFrequency, setCompoundingFrequency] = useState(2); // 2 = Semi-annually
  const [investmentYears] = useState(10);

  const companyTypes = [
    {
      icon: <Building size={32} />,
      title: "Financial Institutions",
      description: "Established financial companies with strong credit ratings and regulatory compliance"
    },
    {
      icon: <Banknote size={32} />,
      title: "Non-Banking Finance Companies (NBFCs)",
      description: "Specialized financial companies offering competitive returns with professional management"
    },
    {
      icon: <Building size={32} />,
      title: "Manufacturing Companies",
      description: "Well-established manufacturing companies with stable cash flows and business operations"
    },
    {
      icon: <Users size={32} />,
      title: "Housing Finance Companies",
      description: "Regulated housing finance companies providing attractive interest rates with good safety"
    }
  ];

  const fdFeatures = [
    {
      title: "Higher Returns",
      description: "Company Fixed Deposits offer comparatively higher returns than traditional bank deposits",
      icon: <TrendingUp size={24} />
    },
    {
      title: "Flexible Tenure",
      description: "Choose from a wide range of tenure options as per your convenience and financial goals",
      icon: <Calendar size={24} />
    },
    {
      title: "Multiple Payout Options",
      description: "Receive interest payments monthly, quarterly, half-yearly, annually, or at maturity",
      icon: <Clock size={24} />
    },
    {
      title: "Non-Transferable Security",
      description: "FD receipts are non-transferable, ensuring no risk of misuse even if they fall into wrong hands",
      icon: <Shield size={24} />
    }
  ];

  const payoutOptions = [
    "At Maturity - Receive principal + accumulated interest",
    "Monthly - Regular monthly income for pensioners/retirees", 
    "Quarterly - Income every 3 months for planning expenses",
    "Half-yearly - Bi-annual payments for medium-term needs",
    "Annually - Yearly income for long-term financial planning"
  ];

  const benefits = [
    "Attractive interest rates higher than bank FDs",
    "Premature encashment facility available",
    "Wide choice of companies and tenures",
    "Diversify risk across multiple companies",
    "No TDS on interest up to certain limits",
    "Simple documentation and easy process"
  ];

  const investmentTips = [
    {
      title: "Diversify Across Companies",
      description: "Spread deposits across multiple companies in different industries to minimize risk"
    },
    {
      title: "Check Credit Ratings",
      description: "Always verify the credit rating and financial health of the company before investing"
    },
    {
      title: "Compare Interest Rates",
      description: "Evaluate interest rates offered by different companies for similar tenure periods"
    },
    {
      title: "Read Terms Carefully",
      description: "Understand premature withdrawal penalties and other terms and conditions"
    }
  ];

  // Mobile detection
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Compound Interest Calculator Functions
  const calculateCompoundInterest = () => {
    const ratePerPeriod = annualRate / 100 / compoundingFrequency;
    const totalPeriods = investmentYears * compoundingFrequency;
    const maturityAmount = principalAmount * Math.pow(1 + ratePerPeriod, totalPeriods);
    const interestEarned = maturityAmount - principalAmount;
    
    return {
      principalAmount,
      maturityAmount,
      interestEarned,
      totalPeriods
    };
  };

  // Handle button actions based on device
  const handleGetFDRecommendations = () => {
    if (isMobile) {
      window.location.href = 'tel:+919904449922';
    } else {
      navigate('/#contact');
    }
  };

  const handleRequestInformation = () => {
    window.location.href = 'mailto:nitumf2017@gmail.com';
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Breadcrumb Navigation */}
      <div className="bg-light-gold py-4">
        <div className="container">
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" className="text-primary-green hover:text-primary-gold transition-colors">
              Home
            </Link>
            <span className="text-medium-gray">/</span>
            <span className="text-primary-green font-medium">Fixed Deposits</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="section-padding bg-gradient-hero">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Link 
                to="/" 
                className="btn-secondary inline-flex items-center gap-2 hover:scale-105 transition-transform"
              >
                <ArrowLeft size={18} />
                Back to Home
              </Link>
            </div>
            
            <div className="text-primary-gold mb-4">
              <Shield size={64} className="mx-auto" />
            </div>
            
            <h1 className="heading-hero mb-6">
              Fixed Deposits & <span className="text-primary-gold">Secure Savings</span>
            </h1>
            
            <p className="text-large mb-8 max-w-3xl mx-auto">
              Prefer stability? Company fixed deposits give you assured returns, flexible tenures, and peace of mind with every deposit.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button 
                onClick={() => {
                  setTimeout(() => {
                    document.getElementById('contact-section').scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }, 100);
                }}
                className="btn-primary"
              >
                <Shield size={20} />
                Start Investing in FDs
              </button>
              <button 
                onClick={() => document.getElementById('fd-types').scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary"
              >
                Explore Options
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What are Fixed Deposits */}
      <section className="section-padding">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h2 className="heading-section mb-6">What are Company Fixed Deposits?</h2>
              <div className="space-y-4 text-body">
                <p>
                  Fixed Deposits are one of the oldest and most common methods of investing. When it comes to assured returns, choosing the right type of savings scheme makes all the difference.
                </p>
                <p>
                  Fixed Deposits let you make the most of value-added benefits as you create wealth at low risk. Company Fixed Deposits offer a fixed rate of return over a predetermined period of time.
                </p>
                <p>
                  These deposits are offered by various types of companies including financial institutions, NBFCs, manufacturing companies, and housing finance companies, each providing different interest rates and tenure options.
                </p>
              </div>
            </div>
            
            <div className="fade-in-up delay-2">
              <div className="bg-cream-bg rounded-3xl p-8 border-2 border-primary-gold border-opacity-20">
                <h3 className="heading-card mb-6 text-center">FD Highlights</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-primary-gold font-poppins mb-2">8-12%</div>
                    <div className="text-sm text-secondary">Interest Rates</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-primary-gold font-poppins mb-2">1-5</div>
                    <div className="text-sm text-secondary">Years Tenure</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-primary-gold font-poppins mb-2">₹10K</div>
                    <div className="text-sm text-secondary">Minimum Amount</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl">
                    <div className="text-2xl font-bold text-primary-gold font-poppins mb-2">100%</div>
                    <div className="text-sm text-secondary">Capital Safety</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Types of Companies */}
      <section id="fd-types" className="section-padding bg-gradient-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Types of Companies Offering Fixed Deposits</h2>
            <p className="text-large max-w-3xl mx-auto">
              Choose from a diverse range of companies across different sectors, each offering unique benefits and competitive returns
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {companyTypes.map((type, index) => (
              <div key={index} className="service-card fade-in-up" style={{animationDelay: `${index * 0.2}s`}}>
                <div className="text-primary-gold mb-4">
                  {type.icon}
                </div>
                <h3 className="heading-card mb-4">{type.title}</h3>
                <p className="text-body">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features and Benefits */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Features & Benefits</h2>
            <p className="text-large max-w-3xl mx-auto">
              Discover the advantages that make company fixed deposits an attractive investment option for conservative investors
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {fdFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 fade-in-up" style={{animationDelay: `${index * 0.2}s`}}>
                <div className="text-primary-gold mb-4">
                  {feature.icon}
                </div>
                <h3 className="heading-card mb-4">{feature.title}</h3>
                <p className="text-body">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Additional Benefits */}
          <div className="bg-light-gold rounded-3xl p-8">
            <h3 className="heading-card mb-6 text-center">Key Investment Benefits</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-primary-gold mt-1 flex-shrink-0" />
                  <span className="text-body">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Interest Payout Options */}
      <section className="section-padding bg-gradient-section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="fade-in-up">
              <h2 className="heading-section mb-6">Flexible Interest Payout Options</h2>
              <p className="text-body mb-6">
                Choose how frequently you want to receive your interest payments based on your financial needs and cash flow requirements.
              </p>
              
              <div className="space-y-4">
                {payoutOptions.map((option, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl">
                    <div className="w-3 h-3 bg-primary-gold rounded-full mt-2 flex-shrink-0"></div>
                    <span className="text-body">{option}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="fade-in-up delay-2">
              <div className="bg-primary-green rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-poppins font-semibold mb-6 text-center">
                  Check the Power of Long-term Compounding
                </h3>
                
                {/* Principal Amount */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">Investment Amount</label>
                    <span className="text-primary-gold font-semibold">₹{principalAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <Slider
                    value={[principalAmount]}
                    onValueChange={(value) => setPrincipalAmount(value[0])}
                    min={10000}
                    max={100000}
                    step={10000}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs mt-1 opacity-75">
                    <span>₹10,000</span>
                    <span>₹1,00,000</span>
                  </div>
                </div>

                {/* Annual Rate */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">Annual ROI</label>
                    <span className="text-primary-gold font-semibold">{annualRate}%</span>
                  </div>
                  <Slider
                    value={[annualRate]}
                    onValueChange={(value) => setAnnualRate(value[0])}
                    min={5}
                    max={30}
                    step={0.5}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs mt-1 opacity-75">
                    <span>5%</span>
                    <span>30%</span>
                  </div>
                </div>

                {/* Compounding Frequency */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">Compounding Frequency</label>
                  </div>
                  <select
                    value={compoundingFrequency}
                    onChange={(e) => setCompoundingFrequency(Number(e.target.value))}
                    className="w-full p-3 rounded-xl bg-white bg-opacity-20 text-white border border-white border-opacity-30 focus:outline-none focus:ring-2 focus:ring-primary-gold"
                  >
                    <option value={1} className="text-primary-green">Annually</option>
                    <option value={2} className="text-primary-green">Semi-annually (Every 6 months)</option>
                    <option value={4} className="text-primary-green">Quarterly (Every 3 months)</option>
                    <option value={12} className="text-primary-green">Monthly</option>
                  </select>
                </div>

                {/* Investment Period */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium">Investment Period</label>
                    <span className="text-primary-gold font-semibold">{investmentYears} years</span>
                  </div>
                  <Slider
                    value={[investmentYears]}
                    onValueChange={(value) => setInvestmentYears(value[0])}
                    min={1}
                    max={30}
                    step={1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs mt-1 opacity-75">
                    <span>1 year</span>
                    <span>30 years</span>
                  </div>
                </div>

                {/* Results */}
                {(() => {
                  const results = calculateCompoundInterest();
                  return (
                    <>
                      <div className="bg-white bg-opacity-20 rounded-2xl p-6 mb-6 text-center">
                        <div className="text-4xl font-bold text-primary-gold mb-2">
                          ₹{Math.round(results.maturityAmount).toLocaleString('en-IN')}
                        </div>
                        <div className="text-sm opacity-90">Will become</div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-white bg-opacity-10 rounded-xl p-4 text-center">
                          <div className="text-lg font-bold text-primary-gold">
                            ₹{Math.round(results.principalAmount).toLocaleString('en-IN')}
                          </div>
                          <div className="text-xs opacity-90">Principal</div>
                        </div>
                        <div className="bg-white bg-opacity-10 rounded-xl p-4 text-center">
                          <div className="text-lg font-bold text-primary-gold">
                            ₹{Math.round(results.interestEarned).toLocaleString('en-IN')}
                          </div>
                          <div className="text-xs opacity-90">Interest Earned</div>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-sm opacity-90 mb-2">
                          With {compoundingFrequency === 1 ? 'Annual' : 
                                compoundingFrequency === 2 ? 'Semi-annual' : 
                                compoundingFrequency === 4 ? 'Quarterly' : 'Monthly'} Compounding
                        </div>
                        <div className="text-xs opacity-75">
                          *This is for illustration purposes only. Actual returns may vary.
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investment Tips */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-section mb-6">Smart Investment Tips</h2>
            <p className="text-large max-w-3xl mx-auto">
              Make informed decisions with these essential tips for company fixed deposit investments
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {investmentTips.map((tip, index) => (
              <div key={index} className="bg-cream-bg rounded-3xl p-8 hover:shadow-xl transition-all duration-300 fade-in-up" style={{animationDelay: `${index * 0.2}s`}}>
                <h3 className="heading-card mb-4">{tip.title}</h3>
                <p className="text-body">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk Diversification */}
      <section className="section-padding bg-primary-green text-white">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-poppins font-semibold mb-6">Diversify Your Risk</h2>
            <p className="text-lg mb-8 opacity-90">
              The deposits should be spread over a large number of companies engaged in different industries. This way, you'll be able to diversify your risk among various industries and companies.
            </p>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-4xl mb-4">🏭</div>
                <h3 className="font-poppins font-semibold mb-3">Manufacturing</h3>
                <p className="text-sm opacity-80">Diversify across different manufacturing sectors</p>
              </div>
              
              <div className="text-center">
                <div className="text-4xl mb-4">🏦</div>
                <h3 className="font-poppins font-semibold mb-3">Financial Services</h3>
                <p className="text-sm opacity-80">Include NBFCs and financial institutions</p>
              </div>
              
              <div className="text-center">
                <div className="text-4xl mb-4">🏠</div>
                <h3 className="font-poppins font-semibold mb-3">Housing Finance</h3>
                <p className="text-sm opacity-80">Add housing finance companies to the mix</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="section-padding bg-light-gold">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="heading-section mb-6">Ready to Secure Your Savings?</h2>
            <p className="text-large mb-8">
              Get expert guidance on selecting the best company fixed deposits that match your risk profile and return expectations.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <Shield size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Safe & Secure</h3>
                <p className="text-body text-sm">Capital protection with assured returns</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <TrendingUp size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Higher Returns</h3>
                <p className="text-body text-sm">Better rates than traditional bank FDs</p>
              </div>
              <div className="bg-white rounded-2xl p-6 text-center">
                <div className="text-primary-gold mb-3">
                  <Users size={32} className="mx-auto" />
                </div>
                <h3 className="font-poppins font-semibold text-primary-green mb-2">Expert Guidance</h3>
                <p className="text-body text-sm">Professional advice on company selection</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={handleGetFDRecommendations}
                className="btn-primary"
              >
                Get FD Recommendations
              </button>
              {isMobile && (
                <button 
                  onClick={handleRequestInformation}
                  className="btn-secondary"
                >
                  Request Information
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FixedDepositsPage;